// Install-to-homescreen support.
//
// Chrome fires beforeinstallprompt instead of showing a dialog, and only lets
// you re-fire it from a user gesture — so the event has to be captured at module
// load and held until someone clicks. Listening from inside a component is too
// late: the event fires around page load, typically before a dropdown has ever
// been opened.
//
// iOS Safari implements none of this. There is no beforeinstallprompt and no way
// to trigger the install from script, so the only thing we can offer there is
// instructions for the Share menu.

let deferredPrompt = null;
let installed = false;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn(getState()));

window.addEventListener('beforeinstallprompt', (event) => {
  // Suppress Chrome's own mini-infobar so the menu item is the single entry
  // point, rather than two competing prompts.
  event.preventDefault();
  deferredPrompt = event;
  notify();
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  installed = true;
  notify();
});

export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true // iOS home-screen launch
  );
}

export function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

// 'installed'    — already running as an installed app, nothing to offer
// 'available'    — Chrome handed us a prompt we can fire
// 'ios'          — iOS Safari: manual Share → Add to Home Screen
// 'unavailable'  — criteria not met yet, or an unsupported browser
export function getState() {
  if (installed || isStandalone()) return 'installed';
  if (deferredPrompt) return 'available';
  if (isIos()) return 'ios';
  return 'unavailable';
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function promptInstall() {
  if (!deferredPrompt) return 'unavailable';

  // A captured prompt is single-use: Chrome discards it once fired, and it only
  // hands us a fresh one on a later page load.
  const event = deferredPrompt;
  deferredPrompt = null;
  notify();

  event.prompt();
  const { outcome } = await event.userChoice;
  return outcome; // 'accepted' | 'dismissed'
}
